import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";

import {
  getObjectDetail,
  getToolInputString,
  parseRecordOrNull,
  quotedText,
} from "../shared";
import { formatDisplayValue, getPreviewListDetail } from "./bedrock-query";
import {
  getInputDetailPart,
  getToolPartOutputRecord,
  naturalDetail,
} from "./bedrock-shared";

export function getDatabaseDetail({
  collectionFormat = "inCollection",
  parts,
  toolPart,
}: {
  collectionFormat?: "inCollection" | "named";
  parts: readonly { label: string; properties: readonly string[] }[];
  toolPart: OpencodeToolPart;
}) {
  const detailParts = parts.map((part) => {
    return getInputDetailPart({
      label: part.label,
      properties: part.properties,
      toolPart,
    });
  });
  const collection = detailParts.find(
    (part) => part.label === "collection",
  )?.value;
  const field = detailParts.find((part) => part.label === "field")?.value;

  if (collection && field) {
    return `${quotedText({ value: field })} in collection ${quotedText({ value: collection })}`;
  }

  if (collection) {
    return collectionFormat === "named"
      ? `named ${quotedText({ value: collection })}`
      : `in collection ${quotedText({ value: collection })}`;
  }

  return naturalDetail({ parts: detailParts });
}

function getDatabaseValuesByKeyDetail({ values }: { values: unknown }) {
  const valuesRecord = parseRecordOrNull(values);
  if (!valuesRecord) {
    return null;
  }

  const entries = Object.entries(valuesRecord).flatMap(([key, value]) => {
    const formattedValue = formatDisplayValue({ value });
    return formattedValue ? [`${key}: ${formattedValue}`] : [];
  });

  return entries.length > 0 ? getPreviewListDetail({ values: entries }) : null;
}

function getDatabaseRecordOutput({ toolPart }: { toolPart: OpencodeToolPart }) {
  return parseRecordOrNull(getToolPartOutputRecord({ toolPart })?.record);
}

export function getDatabaseRecordDetail({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const record = getDatabaseRecordOutput({ toolPart });
  const collectionKey =
    getObjectDetail({ properties: ["collectionKey"], value: record }) ??
    getToolInputString({ properties: ["collectionKey"], toolPart });
  const valuesByKeyDetail = getDatabaseValuesByKeyDetail({
    values:
      record?.valuesByKey ??
      parseRecordOrNull(toolPart.state.input)?.valuesByKey,
  });

  return (
    [
      collectionKey
        ? `in collection ${quotedText({ value: collectionKey })}`
        : null,
      valuesByKeyDetail,
    ]
      .filter((part): part is string => Boolean(part))
      .join(", ") || null
  );
}

export function getDatabaseRecordsDetail({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const outputRecord = getToolPartOutputRecord({ toolPart });
  const records = Array.isArray(outputRecord?.records)
    ? outputRecord.records
    : null;
  const collectionKey = getToolInputString({
    properties: ["collectionKey"],
    toolPart,
  });
  const location = collectionKey
    ? `in collection ${quotedText({ value: collectionKey })}`
    : null;
  let foundDetail: string | null;
  if (records === null) {
    foundDetail = null;
  } else if (records.length === 0) {
    foundDetail = "found none";
  } else {
    foundDetail = `found ${records.length === 1 ? "1 record" : `${records.length} records`}`;
  }

  return [location, foundDetail ? `and ${foundDetail}` : null]
    .filter((part): part is string => Boolean(part))
    .join(", ");
}

export function getDatabaseCollectionsDetail({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const outputRecord = getToolPartOutputRecord({ toolPart });
  const collections = Array.isArray(outputRecord?.collections)
    ? outputRecord.collections
    : null;

  if (!collections) {
    return null;
  }

  if (collections.length === 0) {
    return "and found none";
  }

  return `and found ${
    collections.length === 1
      ? "1 collection"
      : `${collections.length} collections`
  }`;
}
